
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { TrendingUp, Percent, AlertTriangle, UserPlus } from "lucide-react";
import { StrategyWithDetails, StrategyPerformance, Account } from "@/types/strategy-types";

interface ProviderDashboardProps {
  strategies: StrategyWithDetails[];
  accountPerformances?: StrategyPerformance[];
  accounts?: Account[];
}

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
}

const StatCard = ({ title, value, description, icon }: StatCardProps) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">
        {title}
      </CardTitle>
      {icon}
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </CardContent>
  </Card>
);

const ProviderDashboard = ({ 
  strategies,
  accountPerformances = [], 
  accounts = []
}: ProviderDashboardProps) => {
  // Calculate statistics
  const totalStrategies = strategies.length;
  const activeStrategies = strategies.filter(s => s.active).length;
  const proStrategies = strategies.filter(s => s.plan === 'pro' || s.is_pro).length;

  // Calculate total followers by summing the latest entry for each account
  let totalFollowers = 0;
  
  // If we have account performances data, use it to calculate total followers
  if (accountPerformances.length > 0) {
    // Group performances by account_id
    const performancesByAccount = accountPerformances.reduce((acc, perf) => {
      if (!acc[perf.account_id || '']) {
        acc[perf.account_id || ''] = [];
      }
      acc[perf.account_id || ''].push(perf);
      return acc;
    }, {} as Record<string, StrategyPerformance[]>);
    
    // For each account, get the latest performance and sum up num_followers
    Object.values(performancesByAccount).forEach(performances => {
      // Sort by timestamp to get the latest
      const sortedPerformances = performances.sort((a, b) => {
        const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return dateB - dateA; // Descending order (latest first)
      });
      
      // Add the num_followers from the latest performance record
      if (sortedPerformances.length > 0) {
        totalFollowers += sortedPerformances[0].num_followers || 0;
      }
    });
  } else {
    // Fallback to the old calculation if no account performances data
    totalFollowers = strategies.reduce((sum, strategy) => sum + (strategy.followers_count || 0), 0);
  }

  // Calculate average performance
  const strategiesWithPerformance = strategies.filter(s => s.performance_percentage || s.expected_monthly_profit);
  const avgPerformance = strategiesWithPerformance.length > 0
    ? (strategiesWithPerformance.reduce((acc, s) => acc + (s.performance_percentage || s.expected_monthly_profit || 0), 0) / strategiesWithPerformance.length).toFixed(2)
    : 0;

  // Calculate average win rate
  const strategiesWithWinRate = strategies.filter(s => s.win_rate !== null && s.win_rate !== undefined);
  const avgWinRate = strategiesWithWinRate.length > 0
    ? (strategiesWithWinRate.reduce((acc, s) => acc + (s.win_rate || 0), 0) / strategiesWithWinRate.length).toFixed(2)
    : 0;

  // Find highest drawdown
  const maxDrawdown = strategies.reduce((max, s) => {
    const drawdown = s.max_drawdown || s.defined_max_drawdown || 0;
    return drawdown > max ? drawdown : max;
  }, 0);

  // Count lead accounts
  const totalAccounts = accounts.length;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Your Performance Overview</h2>
      
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-3">
        <StatCard
          title="Your Strategies"
          value={totalStrategies}
          description={`${activeStrategies} active, ${proStrategies} pro`}
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          title="Total Followers"
          value={totalFollowers}
          description="Across all strategies"
          icon={<UserPlus className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          title="Average Performance"
          value={`${avgPerformance}%`}
          description="Across all strategies"
          icon={<Percent className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          title="Highest Drawdown"
          value={`${maxDrawdown}%`}
          description={`Average win rate: ${avgWinRate}%`}
          icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          title="Total Accounts"
          value={totalAccounts}
          description="Linked to your strategies"
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
        />
      </div>
    </div>
  );
};

export default ProviderDashboard;
